'use client';

import {
  useCredential,
  useCredentials,
  useSupportedCredentials,
} from '@commenda/api';
import { LoadingIndicator } from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import {
  CredentialsGetExtensibleFields,
  ICredentialBlock,
} from '@commenda/types/src/credential/types';
import { RegistrationHelper } from '@commenda/utils';
import { useEffect, useState } from 'react';
import { CredentialForm } from './CredentialForm';

type CredentialsProps = {
  businessEntity: IBusinessEntity;
  onBack: () => void;
  onContinue: () => void;
};

const IRSFilingCredentials = ({
  businessEntity,
  onBack,
  onContinue,
}: CredentialsProps) => {
  const { data: credentialsResponse, error: credentialsError } = useCredentials(
    {
      businessEntityId: businessEntity.id,
      extends: [CredentialsGetExtensibleFields.SECRETS],
    }
  );

  const irsServicesCredential = credentialsResponse?.credentials?.find(
    (credential) =>
      credential.internalIdentifier ===
      RegistrationHelper.US_IRS_E_SERVICES_INTERNAL_IDENTIFIER
  );

  const { data: credentialResponse, error: credentialError } = useCredential(
    irsServicesCredential?.id,
    {
      includeSecret: true,
    }
  );

  const {
    data: supportedCredentialsResponse,
    error: supportedCredentialsError,
  } = useSupportedCredentials({
    country: businessEntity?.generalCorporationInfo?.incorporationCountry,
  });

  const [notes, setNotes] = useState<string>('');

  const [supportedCredentialBlocks, setSupportedCredentialBlocks] = useState<
    ICredentialBlock[]
  >([]);

  const matchingCredentials =
    supportedCredentialsResponse?.supportedCredentials.filter(
      (cred) =>
        cred.internalIdentifier ===
          RegistrationHelper.US_IRS_E_SERVICES_INTERNAL_IDENTIFIER &&
        cred.country ===
          businessEntity.generalCorporationInfo.incorporationCountry
    ) ?? [];

  // Get unique blocks from all matching credentials
  const uniqueBlocks = matchingCredentials.reduce((acc, cred) => {
    const blockExists = acc.some((b) => b.key === cred.blockName);
    if (!blockExists) {
      acc.push({
        key: cred.blockName,
        type: cred.blockType,
        value: '',
        secretMask: '',
        isCustomBlock: false,
        id: null,
      });
    }
    return acc;
  }, [] as ICredentialBlock[]);

  // Update the effect to properly handle credential updates
  useEffect(() => {
    if (
      !credentialsResponse ||
      !uniqueBlocks ||
      (irsServicesCredential && !credentialResponse)
    )
      return;

    // fill the data in uniqueBlocks with the data from irsServicesCredential
    const updatedUniqueBlocks = uniqueBlocks.map((block) => {
      const irsBlock =
        credentialResponse?.credential?.blocks?.find(
          (b) => b.key === block.key
        ) ?? irsServicesCredential?.blocks?.find((b) => b.key === block.key);

      if (irsBlock) {
        return {
          ...block,
          value: irsBlock.value,
          isCustomBlock: false,
          secretMask: irsBlock.secretMask,
          id: irsBlock.id,
        };
      }
      return block;
    });

    if (
      updatedUniqueBlocks.length > 0 &&
      updatedUniqueBlocks.length !== supportedCredentialBlocks.length
    ) {
      setSupportedCredentialBlocks(updatedUniqueBlocks);
      setNotes(irsServicesCredential?.notes || '');
    }
  }, [
    credentialsResponse,
    credentialResponse,
    irsServicesCredential,
    uniqueBlocks,
    supportedCredentialBlocks,
  ]);

  if (
    !credentialsResponse ||
    !supportedCredentialsResponse ||
    credentialsError ||
    supportedCredentialsError
  ) {
    console.error('supportedCredentialsError', supportedCredentialsError);
    console.error('credentialError', credentialError);
    return (
      <div className="flex flex-col gap-2xl">
        <LoadingIndicator _className="mx-auto" size={48} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2xl">
      <h2 className="text-2xl font-semibold">Credentials</h2>

      <CredentialForm
        notes={notes}
        setNotes={setNotes}
        supportedCredentialBlocks={supportedCredentialBlocks}
        setSupportedCredentialBlocks={setSupportedCredentialBlocks}
        irsServicesCredential={irsServicesCredential}
        businessEntity={businessEntity}
        onBack={onBack}
        onContinue={onContinue}
      />
    </div>
  );
};

export default IRSFilingCredentials;
